import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';

// Ozon/WB-настройки (pvzChatId, тарифы доставки, дедлайн QR, запрещённые
// категории) удалены 2026-06-12 (волна 2 PR5) — модуль Ozon/WB в архиве
// (archive/ozonwb-flow, тег pre-ozonwb-extract).
interface AppSettings {
  ownerNotifyChatId: string | null;
}

const DEFAULTS: AppSettings = {
  ownerNotifyChatId: null,
};

export function AdminSettingsSubscreen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: () => api<AppSettings>('/admin/settings'),
  });

  const [form, setForm] = useState<AppSettings>(DEFAULTS);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const isDirty = data ? form.ownerNotifyChatId !== data.ownerNotifyChatId : false;

  const mutation = useMutation({
    mutationFn: (payload: AppSettings) =>
      api<AppSettings>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(payload),
      }),
    onSuccess: (next) => {
      qc.setQueryData(['admin-settings'], next);
      setForm(next);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    },
  });

  function patch<K extends keyof AppSettings>(key: K, val: AppSettings[K]) {
    setForm((prev) => ({ ...prev, [key]: val }));
  }

  const errMsg =
    mutation.error instanceof ApiError
      ? mutation.error.body?.message ?? mutation.error.message
      : mutation.error
        ? String(mutation.error)
        : null;

  return (
    <div data-testid="admin-settings" className="content" style={{ maxWidth: 820 }}>
      {isLoading ? (
        <div className="panel">Загрузка…</div>
      ) : (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <p className="panel-title" style={{ marginBottom: 6 }}>
              Уведомления в Telegram
            </p>
            <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
              Минус для супергрупп, например <code>-1001234567890</code>. Пусто — фича отключена.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field
                label="Chat ID получателя сводки"
                hint="Куда бот шлёт сервисные уведомления (склад, курьерские shop-заказы, чеки)"
                value={form.ownerNotifyChatId ?? ''}
                onChange={(v) => patch('ownerNotifyChatId', v || null)}
              />
            </div>
          </div>

          {errMsg && (
            <div
              className="panel"
              style={{ borderColor: 'var(--mnp-red)', color: 'var(--mnp-red-dark)', marginBottom: 16 }}
            >
              Ошибка сохранения: {errMsg}
            </div>
          )}

          <div className="f-row" style={{ justifyContent: 'flex-end', gap: 12 }}>
            {savedFlash && <span className="badge-a green">Сохранено</span>}
            <button
              className="btn-admin primary"
              type="button"
              disabled={!isDirty || mutation.isPending}
              onClick={() => mutation.mutate(form)}
            >
              {mutation.isPending ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Field({
  label,
  suffix,
  hint,
  value,
  onChange,
}: {
  label: string;
  suffix?: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--mnp-fg-1)', display: 'block', marginBottom: 4 }}
      >
        {label}
      </label>
      <div className="f-row">
        <input
          className="input-admin"
          style={{ flex: 1 }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className="muted">{suffix}</span>}
      </div>
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  );
}
